/**
 * WB PDP Color Switch — instant colour switching on the sportswear PDP without a
 * full page reload. Ported from oatheme's pdp-color-switch.js and retargeted to
 * womensbest's fashion PDP markup.
 *
 * How it works
 *  1. A click on a colour swatch (rendered by index.layout.js: createHtml / createFirstElement)
 *     is caught by our delegated document listener, which switches in place instead of
 *     letting the swatch's href navigate.
 *  2. We fetch two things for the new handle:
 *       - GraphQL product data via WBProductsApi (id / availability / price / first variant id;
 *         tag-derived content comes from the fragment below, not from here)
 *       - server-rendered fragments via  /products/{handle}?view=pdp-switch
 *         (templates/product.pdp-switch.liquid) — images, badges, sizes, price, colour name,
 *         description tabs, page title
 *  3. The fragments are swapped into the live DOM, the Swiper / size / add-to-cart
 *     handlers are re-bound, history is pushed and `wb:colorswitch` is dispatched.
 *
 * Rendering the real Liquid snippets server-side (rather than rebuilding the markup
 * in JS) keeps every shop / language / length / early-access permutation identical
 * to a normal PDP paint. If anything fails we fall back to normal navigation.
 *
 * Depends on: WBProductsApi (wb-products-api.js), WBStorefrontApi, jQuery, Swiper.
 * Self-contained vanilla JS otherwise. Public API: window.wbPdpColorSwitch.
 */
(function() {
  'use strict';

  var currentHandle = null;
  var switching = false;
  // Switch requested while one was already running; applied once that finishes.
  // { handle, opts } rather than a bare handle: the opts decide whether the replay pushes a
  // history entry, and replaying without them turned a Back press that landed mid-switch
  // into a pushState — so leaving the colour took two Backs instead of one.
  var queued = null;
  // A full page load has been committed. Everything after that point is work on a document
  // that is on its way out, and the shopper watches it happen: an in-place swap that lands
  // during the unload shows them a colour the reload is about to replace.
  var navigating = false;
  // Prefetched ?view=pdp-switch fragments, keyed by handle → { at, promise }.
  // Warmed on hover/focus intent (see init) and consumed by switchColor, so the
  // actual switch reuses an in-flight/resolved fragment instead of fetching on click.
  var htmlCache = {};
  var lastPrefetch = null;

  var SWITCH_VIEW = '?view=pdp-switch';

  // How long a prefetched fragment stays usable. The fragment carries size availability and
  // prices, so an entry cached for the whole session would keep showing a size as buyable
  // long after it sold out. Short enough to bound that, long enough that browsing a colour
  // family still resolves from memory.
  var HTML_CACHE_TTL_MS = 5 * 60 * 1000;

  // Ceiling on how many fragments are held at once. Comfortably above any colour family a
  // shopper works through in one visit, low enough that the map can't grow without bound.
  var HTML_CACHE_MAX = 12;

  // The two PDP swatch rails. Matched by id, which deliberately excludes the quickadd
  // sidebar's rail (#fashion-color-switch-sidebar): that one keeps its own
  // sidebar.fashionColorChange handler and must not be switched in place.
  var SWATCH_CONTAINERS = '#fashion-color-switch, #fashion-color-limited-switch';

  // The element every swap below writes into. A rail only belongs to us if it lives inside
  // one — that is what makes the swapped-in ?view=pdp-switch fragment land on the markup it
  // was rendered for, instead of on whatever else happens to carry the same rail id.
  //
  // The ids in SWATCH_CONTAINERS are not unique to the single-product fashion PDP.
  // snippets/fashion-color-variant-set.liquid:52 renders #fashion-color-switch on the
  // fashion-SET PDP with the same <a id="variant-{handle}"> swatch shape swatchHandle
  // resolves, and snippets/product-nutrition-changer.liquid:329 renders it on
  // templates/product.liquid. Neither can take a single product's fragment. Today neither
  // can reach this code either — layout/theme.liquid:289 loads this script only for
  // `template == 'product.fashion'` — so this is a guard against that gate widening or those
  // rails moving, not a live bug. It is written as containment rather than as a page-level
  // "is this a switchable PDP?" test precisely so it cannot quietly become a tautology: on
  // the one template that loads us the page-level form was always true.
  //
  // Declared here rather than beside the handlers that read it because the swaps need it too:
  // updateActiveColorSwatch used to query the rails document-wide, so it stamped `active` onto
  // exactly the foreign rails the click and hover handlers refuse to service.
  var SWITCH_TARGET = '#wb-fashion';

  // --- Helpers ---

  // Fetch (and memoise) the server-rendered switch fragment for a handle. The switch
  // is gated on this GET, so warming it on intent is what actually makes a click feel
  // instant — the GraphQL data alone doesn't. Dedupes via htmlCache: a hover-started
  // request in flight is reused by the click rather than fired twice.
  function fetchSwitchHtml(handle) {
    var entry = htmlCache[handle];
    if (entry && (Date.now() - entry.at) < HTML_CACHE_TTL_MS) return entry.promise;

    var url = productUrl(handle);
    // No URL means the handle cannot address a product at all (see productUrl), so there is
    // nothing to request. Not cached: there is no request to avoid repeating.
    if (!url) return Promise.resolve(null);

    var promise = fetch(url + SWITCH_VIEW, { credentials: 'same-origin' })
      // A 404 is the store answering the question — Shopify serves an unknown `view` off the
      // default template, so the only way to 404 here is a handle with no product behind it.
      // Anything else that failed (5xx, throttling, a dropped connection) never got that far.
      .then(function(r) {
        if (r.status === 404) return { html: null, definitive: true };
        if (!r.ok) return { html: null, definitive: false };
        return r.text().then(function(text) { return { html: text, definitive: true }; });
      })
      .catch(function() { return { html: null, definitive: false }; })
      .then(function(result) {
        // Drop an *unresolved* failure instead of memoising it for the full TTL. switchColor
        // turns a null fragment into a full page reload, so a single 5xx or dropped
        // connection would keep that colour reloading for five minutes without ever
        // retrying. The identity check leaves a newer entry (or an eviction by
        // pruneHtmlCache) alone.
        //
        // A definitive miss stays cached, as a null. Dropping those too meant a swatch
        // pointing at a handle with no product (an unpublished colour still in the Tagalys
        // index, a stale data-handle) was re-requested on every hover for the life of the
        // page: onSwatchIntent only de-dupes *consecutive* hovers on one swatch, so a
        // pointer moving between two such swatches re-fetched both indefinitely. This is
        // the same distinction WBProductsApi.fetchByHandles draws for the product lookup.
        if (!result.definitive && htmlCache[handle] && htmlCache[handle].promise === promise) {
          delete htmlCache[handle];
        }
        return result.html;
      });

    htmlCache[handle] = { at: Date.now(), promise: promise };
    pruneHtmlCache();
    return promise;
  }

  // An entry is only TTL-checked when its own handle is read again, so nothing ever removes
  // the ones the shopper doesn't come back to. onSwatchIntent warms on pointerover, so a
  // pointer sweeping a 30-colour swatch rail would otherwise pin 30 full PDP fragments —
  // tens of KB of HTML each — for the life of the page. Runs on write: drop what has expired,
  // then cap what's left to the most recently fetched. Evicting an in-flight entry is safe:
  // switchColor holds its own reference to the promise.
  function pruneHtmlCache() {
    var now = Date.now();
    var handles = Object.keys(htmlCache);
    for (var i = 0; i < handles.length; i++) {
      if ((now - htmlCache[handles[i]].at) >= HTML_CACHE_TTL_MS) delete htmlCache[handles[i]];
    }

    handles = Object.keys(htmlCache);
    if (handles.length <= HTML_CACHE_MAX) return;
    handles.sort(function(a, b) { return htmlCache[a].at - htmlCache[b].at; });
    for (var j = 0; j < handles.length - HTML_CACHE_MAX; j++) delete htmlCache[handles[j]];
  }

  // The handle the browser is currently on. Decoded, then normalized, so it is directly
  // comparable to a handle resolved off a swatch: everything downstream works in decoded
  // handles and re-encodes when it builds a URL (productUrl), so handing the raw path segment
  // on would double-encode any handle that needed escaping and would leave the `handle ===
  // currentHandle` guards comparing a raw segment against a normalized one.
  function getHandleFromUrl() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (!match) return null;
    var raw = match[1];
    try {
      raw = decodeURIComponent(raw);
    } catch (e) {
      // Malformed percent-escape: normalize what is there rather than give up entirely.
    }
    return normalizeHandle(raw);
  }

  // Convert data-src/data-srcset → src/srcset so images load once swapped in
  // (LazyLoad won't re-observe nodes injected via innerHTML reliably).
  function activateLazyPictures(root) {
    var sources = root.querySelectorAll('source[data-srcset]');
    for (var s = 0; s < sources.length; s++) {
      sources[s].srcset = sources[s].getAttribute('data-srcset');
    }
    var imgs = root.querySelectorAll('img[data-src]');
    for (var i = 0; i < imgs.length; i++) {
      imgs[i].src = imgs[i].getAttribute('data-src');
      imgs[i].classList.remove('lazy');
      imgs[i].classList.add('loaded');
    }
    if (window.lazyLoadInstance && typeof window.lazyLoadInstance.update === 'function') {
      window.lazyLoadInstance.update();
    }
  }

  // --- Fragment swaps (source: product.pdp-switch.liquid) ---

  function swapColorName(doc) {
    var src = doc.getElementById('wb-switch-color-name');
    var el = document.getElementById('fashion-color-switch-item-title');
    var colorName = src ? src.textContent.trim() : '';
    if (src && el) el.textContent = ' ' + colorName;
    return colorName;
  }

  // The breadcrumb's last link points to the product URL (and its category link
  // is derived from the product's tags), so it must be re-rendered for the new
  // colour. Source: breadcrumbs.liquid (via product.pdp-switch.liquid).
  function swapBreadcrumb(doc) {
    var src = doc.getElementById('breadcrumb');
    var target = document.getElementById('breadcrumb');
    if (src && target) target.innerHTML = src.innerHTML;
  }

  function swapDesktopImages(doc) {
    var src = doc.getElementById('wb-switch-desktop-images');
    var target = document.querySelector('.fashion-info-flex .wb-single-product_image-container');
    if (src && target) {
      target.innerHTML = src.innerHTML;
      activateLazyPictures(target);
    }
  }

  // The desktop zoom overlay's gallery. #product_zoom (product-image-zoom.liquid, painted by
  // product.fashion.liquid outside every swap region) is where the desktop lightbox gets its
  // pixels: showDesktop reads nothing off the image that was clicked but its data-index, then
  // slides the pre-rendered gallery to it. Nothing updated it, so opening zoom after a switch
  // showed the colour the shopper landed on.
  //
  // Only the desktop half is replaced. #product_zoom_mobile is where initMobile() appends its
  // PinchZoom container, and that instance is built once and kept in a closure — replacing the
  // overlay wholesale would leave it bound to a detached node. Mobile needs nothing anyway:
  // showMobile clones the <picture> that was clicked and reads its data-zoom-src off the live
  // DOM, which swapMobileImages has already refreshed.
  //
  // Deliberately not activateLazyPictures(). These are `master`-size sources, up to eight of
  // them, and the Swiper this markup belongs to carries its own lazy loading over .slider-lazy
  // — rewriting them to src up front would pull the full-resolution gallery for every colour
  // the shopper so much as hovered, for an overlay they may never open.
  function swapZoomGallery(doc) {
    var src = doc.querySelector('#wb-switch-zoom .wb-product-zoom-desktop');
    var target = document.querySelector('#product_zoom .wb-product-zoom-desktop');
    if (!src || !target) return;

    // Before the slides go: the Swiper is built lazily on first open and initDesktop()
    // early-returns while it exists, so leaving it in place would hand the new colour an
    // uninitialised stack of slides with no thumbs rail and no lazy loading.
    if (window.wbProductZoom && typeof window.wbProductZoom.resetDesktop === 'function') {
      window.wbProductZoom.resetDesktop();
    }

    target.innerHTML = src.innerHTML;
  }

  function swapMobileImages(doc) {
    var src = doc.getElementById('wb-switch-mobile-images');
    var target = document.querySelector('.product-image-slider-wrapper');
    if (!src || !target) return;

    // Tear down existing mobile Swipers before replacing their DOM.
    destroyMobileSliders();

    target.innerHTML = src.innerHTML;
    activateLazyPictures(target);
    initMobileSliders();
    bindMobileZoomIcons(target);
  }

  function swapTopBadge(doc) {
    var src = doc.getElementById('wb-switch-top-badge');
    var target = document.querySelector('.product-top-wrapper .hidden-xs.hidden-sm');
    if (src && target) target.innerHTML = src.innerHTML;
  }

  function swapPrice(doc) {
    var src = doc.getElementById('wb-switch-price');
    var target = document.querySelector('.wb-product_price-section__col-price');
    if (src && target) target.innerHTML = src.innerHTML;
  }

  // Description tabs. All three of these regions are built in product-fashion-content.liquid
  // from product.tags and the colour half of product.title, so all three can differ per colour:
  // the details copy names the colour and carries the tag-driven logo / capsule-colour notices,
  // size & fit carries the per-colour model line, and material & care appends the per-colour
  // disclaimers ("This item may transfer color…", the Black-only opacity note on Power leggings).
  //
  // Normally only each tab's .product-tabs__content is replaced, never the .product-tabs__item
  // around it: that keeps the accordion's open/closed state across a switch, and product-tabs-js
  // delegates its clicks from .product-tabs, so its handling survives untouched.
  //
  // The item itself is only touched when a region exists on one side and not the other.
  // product-fashion-tabs-view.liquid renders each .product-tabs__item only when its content is
  // non-blank, and the copy is tag- and colour-derived (product-fashion-content.liquid), so a
  // colour can legitimately have no material & care text where the previous one did, or vice
  // versa. Skipping those cases would leave the previous colour's copy on screen — the exact
  // failure the per-tab swap exists to prevent.
  // Source: product-fashion-tabs-view.liquid (via product.pdp-switch.liquid).
  var TAB_CONTENT_IDS = ['wb-tab-details', 'wb-tab-sizefit', 'wb-tab-material'];

  function tabItemOf(content) {
    return content && content.closest ? content.closest('.product-tabs__item') : null;
  }

  function swapProductTabs(doc) {
    var liveTabs = document.querySelector('.product-tabs');

    for (var i = 0; i < TAB_CONTENT_IDS.length; i++) {
      var id = TAB_CONTENT_IDS[i];
      var src = doc.getElementById(id);
      var target = document.getElementById(id);

      if (src && target) {
        target.innerHTML = src.innerHTML;
        // Undo a hide from an earlier switch: the region is back for this colour.
        var shown = tabItemOf(target);
        if (shown) shown.style.display = '';
      } else if (target) {
        // No copy for this region in the new colour. Hide the whole item — header included,
        // since an accordion row that opens onto nothing is worse than no row — and drop the
        // stale text so it can't be read out or scraped while hidden. The element stays in
        // place so a switch back to a colour that has the region can refill it above.
        var hidden = tabItemOf(target);
        target.innerHTML = '';
        if (hidden) hidden.style.display = 'none';
      } else if (src && liveTabs) {
        // The colour the shopper landed on never rendered this region, so there is no target
        // to fill. Clone the server-rendered item into the position the tabs view would have
        // given it. Clicks work immediately — product-tabs-js delegates from .product-tabs.
        insertTabItem(liveTabs, src);
      }
    }

    // Rendered by product-fashion-tabs.liquid from the same f_pp_* strings as the tabs above,
    // so it goes stale for exactly the same reason. Only read by the app content view today
    // (product.app-content.liquid), but keeping it in step costs one assignment.
    var srcJson = doc.getElementById('product_tabs_json');
    var liveJson = document.getElementById('product_tabs_json');
    if (srcJson && liveJson) liveJson.textContent = srcJson.textContent;

    // No re-binding needed: the key-features "show more" toggle is delegated from document
    // (product-keyfeatures-vue.liquid), so it keeps working on swapped-in markup.
  }

  // Put a tab region the live page is missing back in document order, anchored on the nearest
  // preceding tab both sides have (matched by data-tab, which every item carries). Falls back
  // to the front of the list when the live page has none of them.
  function insertTabItem(liveTabs, srcContent) {
    var srcItem = tabItemOf(srcContent);
    if (!srcItem || !srcItem.parentNode) return;

    var siblings = [].slice.call(srcItem.parentNode.children);
    var clone = srcItem.cloneNode(true);

    for (var i = siblings.indexOf(srcItem) - 1; i >= 0; i--) {
      var tab = siblings[i].getAttribute('data-tab');
      var anchor = tab ? liveTabs.querySelector('.product-tabs__item[data-tab="' + tab + '"]') : null;
      if (anchor) {
        anchor.insertAdjacentElement('afterend', clone);
        return;
      }
    }
    liveTabs.insertBefore(clone, liveTabs.firstChild);
  }

  // The key-features bullets under the product title. They are derived from product tags, so
  // they differ per colour, and they sit outside the description tabs (product.fashion.liquid
  // renders the tabs with hide_keyfeatures: true and the block separately inside
  // .product-top-wrapper) — so swapProductTabs does not cover them.
  //
  // Copying innerHTML keeps the live .product-keyfeatures wrapper in place and drops the
  // previous colour's expanded state, which is what a normal page load would also show.
  // Source: product-keyfeatures-vue.liquid (via #wb-switch-keyfeatures).
  function swapKeyFeatures(doc) {
    var src = doc.querySelector('#wb-switch-keyfeatures .product-keyfeatures');
    var target = document.querySelector('#wb-fashion .product-top-wrapper .product-keyfeatures');
    if (src && target) target.innerHTML = src.innerHTML;
  }

  // Cross-product length / style switcher (leggings <-> 7/8, enhanced <-> scrunch,
  // logo variants, ...). Its links are computed from the current handle, so they
  // must be re-rendered for the new colour. Source: product-fashion-changer.liquid.
  function swapChanger(doc) {
    var src = doc.getElementById('wb-switch-changer');
    var target = document.getElementById('wb-fashion-changer');
    if (src && target) target.innerHTML = src.innerHTML;
  }

  // Rebuilds the size / length UI from the server render, then callers re-bind handlers.
  function swapSizes(doc) {
    var src = doc.getElementById('wb-switch-sizes');
    var sizeContainer = document.querySelector('.wb-single-product_meta-container .product-sizes-container');
    var variantsWrap = sizeContainer ? sizeContainer.closest('.product-variants') : null;
    if (!variantsWrap) {
      // single-variant products render a hidden <select>; find the size .product-variants
      // (the colour switch lives in its own .product-variants which we must not touch)
      var candidates = document.querySelectorAll('.wb-single-product_meta-container form .product-variants');
      for (var i = 0; i < candidates.length; i++) {
        if (!candidates[i].querySelector('#fashion-color-switch')) { variantsWrap = candidates[i]; break; }
      }
    }
    if (!src || !variantsWrap) return;

    // The FitAnalytics fit-finder button is injected client-side as the last child of
    // .product-sizes-container, so it isn't part of this server render. Reserve the
    // container's current height as a min-height before swapping, so it doesn't collapse
    // while the button is torn down and re-injected — otherwise the price / add-to-cart
    // below it jump up and back down (CLS). Using min-height (not a fixed height) keeps
    // it dynamic: taller content in the new colour is never clipped.
    var oldContainer = variantsWrap.querySelector('.product-sizes-container');
    var reservedHeight = oldContainer ? oldContainer.getBoundingClientRect().height : 0;

    variantsWrap.innerHTML = src.innerHTML;

    if (reservedHeight > 0) {
      var newContainer = variantsWrap.querySelector('.product-sizes-container');
      if (newContainer) newContainer.style.minHeight = reservedHeight + 'px';
    }
  }

  // --- Mobile Swiper (mirrors setupProductImageSlider in product-single.layout.js) ---

  var mobileSliderIds = ['product_image_slider_mobile', 'product_image_slider_mobile2'];

  function destroyMobileSliders() {
    mobileSliderIds.forEach(function(id) {
      var el = document.getElementById(id);
      if (el && el.swiper) el.swiper.destroy(true, true);
      var thumbs = document.getElementById(id + '_thumbs');
      if (thumbs && thumbs.swiper) thumbs.swiper.destroy(true, true);
    });
  }

  // Rebuilt through product-single.layout.js's own factory rather than a second config kept
  // here. The copy this replaces had already drifted from the real one: it had lost the
  // slideChange handler that unpins the header under 1300px, the queueEndCallbacks flag and
  // the thumbs rail's navigation arrows — so the mobile gallery behaved differently on a
  // switched-to colour than on a page load of the same URL, silently. Same export pattern as
  // bindMobileZoomIcons below, and for the same reason.
  function initMobileSliders() {
    if (typeof Swiper === 'undefined') return;
    if (typeof window.wbSetupProductImageSlider !== 'function') return;
    mobileSliderIds.forEach(function(id) { window.wbSetupProductImageSlider(id); });
  }

  // Mobile zoom trigger. The expand icon sits inside the swapped markup, so it comes back
  // unbound on every switch. The handler itself belongs to product-single.layout.js, which
  // binds it at load and exports the binder — calling that rather than keeping a second copy
  // of the target selector here, which would silently stop matching the next time the mobile
  // zoom target moves (it already has once, in b0d571a7b). Scoped to the swapped region:
  // anything outside it kept the listener it was bound with at load.
  //
  // The Swiper this replaces has no `on.click` on purpose: tapping the image itself opened the
  // lightbox until b0d571a7b made the icon the only trigger, and re-adding it here would bring
  // that back for switched-to colours only.
  function bindMobileZoomIcons(root) {
    if (typeof window.wbBindMobileZoomIcons === 'function') window.wbBindMobileZoomIcons(root);
  }

  // --- Size + Add-to-cart handlers (mirrors setupFashionView) ---

  function reinitSizeHandlers() {
    var $ = window.jQuery;
    if (!$) return;
    var $input = $('#variant-id');

    // Size <li> selection (length tabs keep their inline onclick=selectFilterProductsBySize).
    $('.fashion-info-sticky ul.product-sizes li').each(function() {
      var $li = $(this);
      $li.off('click.wbSwitch').on('click.wbSwitch', function() {
        if ($li.hasClass('disabled')) return;
        var variantId = $li.data('size');
        var variantSku = $li.data('sku');
        var variantPrice = $li.data('price');
        var variantCompare = $li.data('compareprice');

        $('.fashion-info-sticky ul.product-sizes li').removeClass('active');
        $li.addClass('active');
        $('.product-sizes').removeClass('red');
        $('.choose-size-alert').hide(100);
        $('.product-add-to-cart.is-sticky').removeClass('product-add-to-cart-error');

        if ($li.data('set')) return;

        // Resolved on each click rather than captured when the handler is bound: a colour
        // switch replaces the add-to-cart nodes (swapAtc), so a captured collection would
        // go on toggling `disabled` on detached buttons.
        var $addToCartBtn = $('.wb-product-addtocart-btn');

        if (variantId) {
          $addToCartBtn.attr('disabled', false);
          $input.val(variantId);
          $input.attr('data-sku', variantSku);
          $('#wb-fashion .fashion-info-flex #in-den-warenkorb').removeClass('select');
          document.dispatchEvent(new CustomEvent('wb:variantchange', {
            detail: { sportswear: true, sku: variantSku, id: variantId, name: $li.data('title') }
          }));
          // Defer to the theme's own price updater (product-single.layout.js) so a size pick
          // after a colour switch formats money exactly like one before it.
          if (variantPrice && variantCompare && typeof window.wbUpdateProductPrice === 'function') {
            window.wbUpdateProductPrice(variantPrice, variantCompare);
          }
        } else {
          $addToCartBtn.attr('disabled', true);
        }
      });
    });

    reinitAtcHandlers();

    // The GTM impression handlers were bound to the size <li>s that swapSizes just replaced, so
    // they have to be reattached or productDetailImpression stops firing on size clicks for the
    // rest of the session. Idempotent (namespaced + .off()).
    if (typeof window.wbTrackingRebind === 'function') window.wbTrackingRebind();
  }

  function reinitAtcHandlers() {
    var $ = window.jQuery;
    if (!$) return;

    // Block submit until a size is chosen. Mirrors setupFashionView's gate.
    $('.wb-product-addtocart-btn:not(.wb-product-addtocart-btn-cts, .product-stl__submit-button)')
      .off('click.wbSwitchGate').on('click.wbSwitchGate', function(event) {
        if ($('.wb-product-addtocart-btn').hasClass('early-access-cta')) return;
        if ($('#variant-id').val() === '') {
          event.preventDefault();
          $('.product-sizes:not(.product-sizes-cts, .product-stl-sizes)').addClass('red');
          $('.choose-size-alert').show(100);
          $('.product-add-to-cart.is-sticky:not(.product-add-to-cart-cts)').addClass('product-add-to-cart-error');
        }
      });

    // Sticky ATC on mobile scrolls up to the size picker.
    $('.is-sticky #in-den-warenkorb.select').off('click.wbSwitchScroll').on('click.wbSwitchScroll', function() {
      if (!$(this).hasClass('select')) return;
      var flex = $('.fashion-info-flex');
      if (!flex.length) return;
      $([document.documentElement, document.body]).animate({ scrollTop: flex.offset().top - 100 }, 500);
    });
  }

  // Re-pick the size the shopper had selected, if it exists and is buyable in the
  // new colour (WB filters the visible sizes by the active length tab).
  function reselectSize(sizeName) {
    if (!sizeName || !window.jQuery) return;
    var items = document.querySelectorAll('.fashion-info-sticky ul.product-sizes li[data-title="' + sizeName + '"]');
    for (var i = 0; i < items.length; i++) {
      var li = items[i];
      if (!li.classList.contains('disabled') && li.offsetParent !== null) {
        // This click is ours, not the shopper's. A real page load never produces one, so it
        // must not report a productDetailImpression on top of the switch's product view. The
        // flag is read synchronously by onDetailImpression, so clearing it as soon as the
        // trigger returns is enough; try/finally keeps a throwing handler from leaving
        // impressions suppressed for the rest of the session.
        window.wbSuppressDetailImpression = true;
        try {
          window.jQuery(li).trigger('click');
        } finally {
          window.wbSuppressDetailImpression = false;
        }
        return;
      }
    }
  }

  // --- Add to cart (available <-> sold out) ---

  // Swap in the server-rendered add-to-cart for the new colour. Both blocks (desktop and the
  // mobile sticky bar) come from product-fashion-addtocart.liquid, the same snippet
  // product.fashion.liquid renders, so every state the theme distinguishes comes across
  // intact: buyable vs. sold out, the disabled "Sold Out" that disable_notify (discontinued /
  // birthday-edition) requires instead of the notify-me button, the `select` class only when
  // the colour has more than one variant, and the localised labels — none of which a
  // JS-rebuilt button reproduced correctly.
  var ATC_IDS = ['wb-switch-atc-desktop', 'wb-switch-atc-sticky'];

  function swapAtc(doc) {
    // The early-access CTA class is applied at runtime by the loyalty integration, not by the
    // theme, so it is not part of the server render. Carry it over rather than letting a
    // colour switch quietly turn a locked CTA back into a plain add-to-cart.
    var wasEarlyAccess = !!document.querySelector('.wb-product-addtocart-btn.early-access-cta');

    for (var i = 0; i < ATC_IDS.length; i++) {
      var src = doc.getElementById(ATC_IDS[i]);
      var target = document.getElementById(ATC_IDS[i]);
      if (!src || !target) continue;
      target.innerHTML = src.innerHTML;
    }

    if (wasEarlyAccess) {
      var btns = document.querySelectorAll('.wb-product-addtocart-btn');
      for (var b = 0; b < btns.length; b++) btns[b].classList.add('early-access-cta');
    }

    reinitAtcHandlers();
  }

  // The add-to-cart fragment already encodes how the server treats notify-me for this colour,
  // so both questions below are answered from it rather than from a second flag:
  //   .btn-soldout             -> sold out
  //   .btn-soldout.disabled    -> sold out AND disable_notify (discontinued / birthday-edition),
  //                               where the theme deliberately offers no notify-me at all
  function fragmentNeedsNotifyModal(doc) {
    return !!doc.querySelector('#wb-switch-atc-desktop .btn-soldout:not(.disabled), #wb-switch-atc-sticky .btn-soldout:not(.disabled)');
  }

  function fragmentDisablesNotify(doc) {
    return !!doc.querySelector('#wb-switch-atc-desktop .btn-soldout.disabled, #wb-switch-atc-sticky .btn-soldout.disabled');
  }

  // Swap the modal's per-colour fields: the size list (built from the colour's *unavailable*
  // variants), the variant SKU and the return link. Source: product-soldout-notify-variants.liquid,
  // the same snippet product-soldout-notify.liquid renders at page load.
  //
  // Only #wb-switch-notify moves — the modal shell and #wb_notify-form around it stay put, so
  // the form's submit handler survives. The size <li>s inside are new nodes, so their click
  // handler is reattached separately by reinitNotifyHandlers.
  function swapNotify(doc) {
    var src = doc.getElementById('wb-switch-notify');
    var target = document.getElementById('wb-switch-notify');
    if (src && target) target.innerHTML = src.innerHTML;
  }

  // Reattach the notify-me flow after the swaps. It binds three node sets a switch replaces —
  // the modal's own size list (swapNotify), the add-to-cart buttons (swapAtc) and the main
  // size list's disabled <li>s, which is what opens the modal in the first place (swapSizes).
  // Bound once on load, as it was, the whole flow went dead after the first switch.
  // Idempotent (namespaced + .off()), so re-running it per switch is safe.
  //
  // Skipped — and actively unbound — for a colour the server marks disable_notify, so that
  // switching into one can't leave a disabled size opening a modal the shopper would never
  // have been offered had they loaded that colour directly. The freshly swapped size list
  // carries no .notifyMe badges either, since only setupNotifyMe adds them.
  function reinitNotifyHandlers(doc) {
    var $ = window.jQuery;
    if (fragmentDisablesNotify(doc)) {
      if ($) $('.product-variants li.disabled').off('click.wbNotify');
      return;
    }
    if (typeof window.wbNotifySetup === 'function') window.wbNotifySetup();
  }

  // --- Data-driven updaters (from GraphQL product) ---

  // Scoped to the add-to-cart form on purpose: [data-productid] is not unique to this
  // product. product-complete-the-set-modal.liquid renders #variant-id_1/2/3 carrying
  // *other* products' ids, and it sits outside this form — a document-wide query would
  // stamp the switched-to colour's id over all three.
  function updateFormProductId(product) {
    var form = document.getElementById('add-to-cart-form');
    if (!form) return;
    form.setAttribute('data-productid', product.id);
    var inputs = form.querySelectorAll('[data-productid]');
    for (var i = 0; i < inputs.length; i++) inputs[i].setAttribute('data-productid', product.id);
  }

  // Scoped to SWITCH_TARGET, the same containment the click and hover handlers apply. Without
  // it this reached rails those handlers deliberately refuse to service — the fashion-SET PDP
  // and product.liquid render #fashion-color-switch too — and cleared and re-applied `active`
  // on a rail whose colours this switch knows nothing about.
  function updateActiveColorSwatch(handle) {
    var root = document.querySelector(SWITCH_TARGET);
    if (!root) return;
    var swatches = root.querySelectorAll(
      '#fashion-color-switch .item, #fashion-color-switch .sidebar--color-item-box,' +
      '#fashion-color-limited-switch .item, #fashion-color-limited-switch .sidebar--color-item-box'
    );
    for (var i = 0; i < swatches.length; i++) {
      var box = swatches[i];
      var inner = box.querySelector('.sidebar--color-item, a[id^="variant-"]');
      if (inner) inner.classList.remove('active');
      box.classList.remove('active');

      var bg = box.querySelector('.sidebar--color-item-bg[data-handle]');
      var link = box.querySelector('a[id^="variant-"]');
      // Compare normalized on both sides. These attributes hold a raw handle on some rails
      // and a full product URL on others, and the strict comparisons this replaces were
      // also case-sensitive — so a data-handle whose casing differed from the switched-to
      // handle matched nothing and the rail was left with no swatch marked active.
      var matches =
        (bg && normalizeHandle(bg.dataset.handle) === handle) ||
        (link && normalizeHandle(link.id.slice('variant-'.length)) === handle);
      if (matches) {
        if (inner) inner.classList.add('active');
        box.classList.add('active');
      }
    }
  }

  function updateTitle(colorName, metaTitle, productTitle) {
    // The base style name is identical across colour variants — only the colour subline needs
    // to change. Scoped to #wb-fashion: a bare '.product-title' also matches the cart-item
    // titles the sidecart renders ahead of the PDP in the DOM (vue-template-cart-item-title),
    // so an unscoped query can land on a completely unrelated product.
    var subline = document.querySelector('#wb-fashion .product-title .product-subline__0');
    if (subline) subline.textContent = colorName;

    // The tab title has to go through the same formatter a real page load uses. On a product
    // page tab-text-modification.liquid overwrites the server-rendered <title> before paint,
    // so meta_title — the SEO title — is never what the shopper actually sees. Using it here
    // is what made a switched-to colour show a different title from a reload of the same URL.
    if (productTitle && typeof window.wbSetTabTitle === 'function') {
      window.wbSetTabTitle(productTitle);
    } else if (metaTitle) {
      // Fallback for any product page that does not render that snippet. Left as-is if the
      // fragment came back empty too — a stale tab title beats a mangled one.
      document.title = metaTitle;
    }
  }

  // Keeps the address bar, the history entry and <link rel=canonical> naming the colour that
  // is actually on screen.
  //
  // `pushState === false` means "the shopper's own navigation already moved the address bar
  // here" — the popstate path. That can stop being true between the request and the swap: a
  // switch still in flight when Back is pressed lands first and pushes its own entry, so by
  // the time the queued popstate colour is replayed the URL names the other product. Taking
  // the flag at its word then left location, canonical and DOM disagreeing, and a reload or a
  // shared link handed over a colour the shopper was not looking at. Verified instead, and
  // corrected in place: the Back press has already moved the history pointer, so pushing
  // again would put an extra press between the shopper and where they came from.
  function updateUrl(handle, pushState) {
    var newUrl = productUrl(handle);
    if (!newUrl) return;
    if (pushState === false) {
      if (window.location.pathname !== newUrl) history.replaceState({ handle: handle }, '', newUrl);
    } else {
      history.pushState({ handle: handle }, '', newUrl);
    }
    var canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = window.location.origin + newUrl;
  }

  function updateWishlist(product) {
    var variantId = product.variants && product.variants[0] ? product.variants[0].id : null;

    // The .btn--addtowishlist-* elements are the wishlist mount containers. Update
    // both the legacy iWish attributes (data-product / data-variant / data-ptitle)
    // and, crucially, data-product-handle: Swish (Wishlist King) is handle-driven —
    // it resolves the product from data-product-handle, the same attribute the theme
    // binds reactively (:data-product-handle) on its dynamic product cards. Without a
    // fresh handle the app keeps showing the colour it first mounted with.
    var btns = document.querySelectorAll('.iWishAddColl, .btn--addtowishlist-desktop, .btn--addtowishlist-mobile');
    for (var i = 0; i < btns.length; i++) {
      btns[i].setAttribute('data-product-handle', product.handle);
      btns[i].setAttribute('data-product-id', product.id);
      btns[i].setAttribute('data-product', product.id);
      if (variantId) {
        btns[i].setAttribute('data-variant-id', variantId);
        btns[i].setAttribute('data-variant', variantId);
      }
      btns[i].setAttribute('data-ptitle', product.title);
    }

    // Swish injects its own <wishlist-button-product> element inside those containers.
    // Because a colour switch never reloads the page, the app never re-resolves that
    // element for the new colour, so it keeps rendering (and wishlisting) the previous
    // product. Point the element's own input attributes at the switched-to colour so it
    // re-renders against the correct product / variant.
    var wishlistEls = document.querySelectorAll(
      '.btn--addtowishlist-desktop wishlist-button-product,' +
      '.btn--addtowishlist-mobile wishlist-button-product'
    );
    for (var j = 0; j < wishlistEls.length; j++) {
      wishlistEls[j].setAttribute('data-product-handle', product.handle);
      wishlistEls[j].setAttribute('data-product-id', product.id);
      if (variantId) wishlistEls[j].setAttribute('data-variant-id', variantId);
    }
  }

  // #wb-fashion carries the colour's variant ids for downstream consumers, so it has to move
  // with the colour rather than keep advertising the ids of the colour the shopper landed on.
  function swapVariantIds(doc) {
    var src = doc.getElementById('wb-switch-variantids');
    var target = document.getElementById('wb-fashion');
    if (src && target) target.setAttribute('data-variantids', src.textContent.trim());
  }

  // Head metadata: description, og/twitter share tags and the Product structured data. A
  // colour switch rewrites the URL, so anything scraping or sharing the page after one would
  // otherwise describe the colour the shopper landed on. Source: #wb-switch-head, which
  // renders the same theme-meta-info / theme-jsonld snippets layout/theme.liquid uses.
  //
  // Tags are matched by their property/name and updated in place (or appended if the new
  // colour introduces one), rather than replacing the head wholesale — the head also holds
  // scripts and styles from apps that must not be touched.
  //
  // Nothing is ever removed, and it doesn't need to be: theme-meta-info's product branch
  // emits the same eleven keys (og:title / og:type / og:url / og:image / og:description,
  // twitter:card / url / site / title / description / image) for every product, so no tag
  // this fragment owns can be present for one colour and absent for the next. The only tags
  // a "remove what the fragment omits" pass would ever find are app-injected ones, which is
  // exactly what must be left alone. Revisit if that snippet ever renders a tag conditionally.
  function swapHeadMeta(doc) {
    var src = doc.getElementById('wb-switch-head');
    if (!src) return;

    var metas = src.querySelectorAll('meta[property], meta[name]');
    for (var i = 0; i < metas.length; i++) {
      var meta = metas[i];
      var attr = meta.hasAttribute('property') ? 'property' : 'name';
      var key = meta.getAttribute(attr);
      if (!key) continue;
      var live = document.head.querySelector('meta[' + attr + '="' + key + '"]');
      if (live) {
        live.setAttribute('content', meta.getAttribute('content') || '');
      } else {
        document.head.appendChild(meta.cloneNode(true));
      }
    }

    // Only the Product blob is colour-dependent; the WebSite / Organization / breadcrumb
    // blobs alongside it are matched by type so the others are left alone.
    var newLd = findProductLdJson(src);
    var liveLd = findProductLdJson(document);
    if (newLd && liveLd) liveLd.textContent = newLd.textContent;
  }

  function findProductLdJson(root) {
    var scripts = root.querySelectorAll('script[type="application/ld+json"]');
    for (var i = 0; i < scripts.length; i++) {
      try {
        var data = JSON.parse(scripts[i].textContent);
        if (data && data['@type'] === 'Product') return scripts[i];
      } catch (e) {
        /* a malformed blob is not the one we're looking for */
      }
    }
    return null;
  }

  // GTM / Emarsys identity for the new colour. Must run before updateTracking and before
  // reselectSize, since both read it (directly or through getSelectedProduct).
  // Source: #wb-switch-tracking, rendered by the same snippet as the page-load value.
  function swapTrackingData(doc) {
    var src = doc.getElementById('wb-switch-tracking');
    if (!src) return;
    try {
      var data = JSON.parse(src.textContent);
      if (!data) return;
      window.wbTrackingProduct = data;
      if (data.emsDetailId) window.ems_detailid = data.emsDetailId;
    } catch (e) {
      // Keep the previous colour's identity rather than tracking against a broken object.
      console.error('WB color switch: could not parse tracking data', e);
    }
  }

  function updateTracking(product) {
    if (window.dataLayer) {
      // A colour switch counts as a product view. The detail payload goes first so the
      // pageview tag reads the new colour's ecommerce object rather than the old one.
      if (typeof window.wbTrackProductDetail === 'function') window.wbTrackProductDetail();

      window.dataLayer.push({
        event: 'virtualPageview',
        // Built the same way as the URL updateUrl pushed, so the pageview reports the path
        // the shopper is actually on.
        // productUrl is null only for a handle it cannot encode; updateUrl has already run
        // by this point, so the current path is the URL that was just pushed.
        page: productUrl(product.handle) || window.location.pathname,
        productId: product.id,
        productTitle: product.title
      });
    }
    if (typeof window.nostojs === 'function') {
      window.nostojs(function(api) { api.loadRecommendations(); });
    }
    var yotpo = document.querySelectorAll('[data-yotpo-product-id]');
    for (var i = 0; i < yotpo.length; i++) yotpo[i].setAttribute('data-yotpo-product-id', product.id);
  }

  // Register the switched-to colour as "recently viewed", just as a normal PDP load
  // would. A colour switch never reloads the page, so the RecentlyViewedProducts
  // component (product-recently-viewed.liquid) never runs its mounted() setRV() for
  // the new handle — the colours a shopper flips through would otherwise be lost.
  // The component is a Vue 2 instance mounted on #product-rv; we reach it via
  // __vue__ (the same access pattern vue-init.liquid uses). Updating productHandle
  // first keeps the now-current colour out of its own carousel, matching how the
  // component behaves on a real page view. No-ops if the component isn't mounted.
  function updateRecentlyViewed(handle) {
    var rvEl = document.getElementById('product-rv');
    var vm = rvEl && rvEl.__vue__;
    if (!vm || typeof vm.setRV !== 'function') return;
    vm.productHandle = handle;
    vm.setRV(handle);
  }

  // Re-resolve "Complete the look" for the new colour. The block is a Vue 2 root instance
  // mounted on #wb-product-ctl (product-complete-the-look.liquid) that fetches its outfit once,
  // from the handle it mounted with (/search?view=cabs-data&q={handle}). A colour switch never
  // reloads the page, so it otherwise keeps offering — and quick-adding — the colour the
  // shopper landed on. Repoint it at the new handle and let it re-run its own fetch, exactly as
  // a page load would. Reached via __vue__, the same access pattern as updateRecentlyViewed.
  //
  // productHandle is declared as a prop, but this is a root instance, so assigning to it is
  // both allowed and reactive — Vue's "avoid mutating a prop" warning is skipped for the root.
  //
  // The bookkeeping below is keyed by product id and only ever appended to (see the `products`
  // watcher), so it's reset here: without that, every switch leaves the previous colour's
  // outfit entries — including any sizes the shopper had picked — behind in the maps.
  // `loading` goes back on so the skeleton covers the re-fetch instead of the old colour's
  // items sitting there; the child items clear it again exactly as they do on first paint.
  function updateCompleteTheLook(product) {
    var ctlEl = document.getElementById('wb-product-ctl');
    var vm = ctlEl && ctlEl.__vue__;
    if (!vm || typeof vm.fetchCabProducts !== 'function') return;
    if (vm.productHandle === product.handle) return;

    vm.productHandle = product.handle;
    vm.loading = true;
    vm.products = [];
    vm.productIds = [];
    vm.selectedProductIds = {};
    vm.selectedCabProductPrices = {};
    vm.errors = [];
    vm.fetchCabProducts();
  }

  // Recompute the "Best Points" reward value for the new colour's price. The
  // helper is defined in product-bestpoints.liquid and expects a price in cents.
  function updateBestPoints(product) {
    if (typeof window.updatePPBestPoints === 'function') {
      window.updatePPBestPoints(product.price);
    }
  }

  // Refresh FitAnalytics for the new colour. The widget is only a click-triggered
  // popup, so rather than live-patching the rendered widget we take the far more
  // robust route the theme already uses elsewhere: re-run the server-rendered
  // fit-finder definitions (product.pdp-switch.liquid -> #wb-switch-fitfinder) to
  // fully replace window.fitAnalyticsData — id, preview image, sizes/availability,
  // sibling product ids — then reinitialise the integration so its button rebuilds
  // for the new serial. Source of the definitions: fit-finder-data.liquid.
  function swapFitFinder(doc) {
    var src = doc.getElementById('wb-switch-fitfinder');
    if (!src || !window.jQuery) return;

    // Tear down the previously injected fit-finder block before adding a new one so
    // the old <script> node doesn't linger. Its observers and collection sweep are
    // cleaned up by the fresh run (fit-finder-data.liquid disconnects the prior
    // observers via window.wbFitFinderObservers and caches the family ids), and
    // removing the stale container keeps the DOM from growing with every switch.
    var prev = document.getElementById('wb-fitfinder-injected');
    if (prev && prev.parentNode) prev.parentNode.removeChild(prev);

    var container = document.createElement('div');
    container.id = 'wb-fitfinder-injected';
    document.body.appendChild(container);

    // jQuery executes the injected <script>s, redefining window.fitAnalyticsData
    // (and re-attaching the button label/tracking setup, which now runs on inject).
    window.jQuery(container).append(src.innerHTML);

    // The integration loader (pdp.js) stays resident from the first paint; point it
    // at the refreshed data and let it rebuild its button.
    reloadFitAnalytics(String((window.fitAnalyticsData && window.fitAnalyticsData.currentProductId) || ''));
  }

  // Reinitialise the FitAnalytics widget against the freshly swapped definitions.
  //
  // Prefer updateCurrentProductId(): it repoints the widget at the new colour's serial
  // WITHOUT removing the button from the DOM. A stop()/start() rebuild pulls the button
  // out and injects a fresh one, so its box collapses to 0 for a beat — the fixed-height
  // slot (fit-finder-data.liquid: wbFitFinderSetup) holds that space either way, so
  // nothing around it moves. window.fitAnalyticsData was already fully replaced (id /
  // preview image / sizes / availability) before this call, and the widget reads it live
  // when opened, so the in-place update stays correct for the new colour.
  function reloadFitAnalytics(serial) {
    var integ = window._fitAnalytics && window._fitAnalytics.integration;
    if (!integ) return;
    try {
      if (serial && typeof integ.updateCurrentProductId === 'function') {
        integ.updateCurrentProductId(serial);
      } else if (typeof integ.start === 'function') {
        var btn = document.querySelector('.fitanalytics__button');
        if (btn && btn.parentNode) btn.parentNode.removeChild(btn);
        if (typeof integ.stop === 'function') { try { integ.stop(); } catch (e2) {} }
        integ.start();
        // This branch removes the button, so the setup that just ran (against the button
        // that was still there) bound a node we have since thrown away. Re-run it now that
        // the DOM has none: it takes the observer path and picks up the replacement button
        // with its custom label and click tracking intact.
        if (typeof window.wbFitFinderSetup === 'function') window.wbFitFinderSetup();
      }
    } catch (e) {
      /* leave the previous widget in place if no reinit path is available */
    }
  }

  // --- Loading state ---

  function showLoading() {
    var el = document.getElementById('wb-fashion');
    if (el) el.classList.add('wb-color-switching');
  }
  function hideLoading() {
    var el = document.getElementById('wb-fashion');
    if (el) el.classList.remove('wb-color-switching');
  }

  // --- Main API ---

  // `requested` is anything handle-ish: a bare handle, /products/{handle}, or a full product
  // URL. Normalizing here rather than trusting the caller is what keeps the three entry points
  // agreeing — onSwatchClick already normalizes via swatchHandle, but popstate and the public
  // window.wbPdpColorSwitch.switchColor did not, and an un-normalized handle reaches
  // fetchSwitchHtml as '%2Fproducts%2Fwb-blue', 404s, and falls back to navigating to that
  // same broken URL.
  function switchColor(requested, opts) {
    opts = opts || {};
    // A full page load is already committed; nothing started now would outlive it.
    if (navigating) return;

    // Before normalizeHandle, not after. normalizeHandle delegates to WBProductsApi and
    // returns null without it, so this branch used to sit behind a `!handle` return that
    // always fired first — leaving the public switchColor and the popstate handler as silent
    // no-ops on a page where the script failed to load, instead of the full navigation this
    // degradation is supposed to be. Nothing here can normalize, so the raw input is used:
    // a path or URL is navigated to as given, anything else is treated as a bare handle.
    if (!window.WBProductsApi) {
      var raw = typeof requested === 'string' ? requested.trim() : '';
      // Same first step normalizeHandle takes: drop an origin, leaving a path or a bare
      // handle. A rooted path is navigated to as given; '//host' is protocol-relative and
      // would leave the store, so it is not one.
      var path = raw.replace(/^https?:\/\/[^/]+/i, '');
      if (!path) return;
      if (path.charAt(0) === '/' && path.charAt(1) !== '/') { navigateTo(path); return; }
      try {
        // Off localeRoot(), not a bare '/products/'. The EU store serves its Spanish
        // storefront under /es (shop-settings.liquid), and dropping that prefix is the same
        // locale loss WBProductsApi.productUrl exists to prevent: it sends the shopper to the
        // English render of the colour they picked. window.Shopify.routes.root comes from
        // Shopify itself, so it is still there on the very path where WBProductsApi is not.
        navigateTo(localeRoot() + 'products/' + encodeURIComponent(path));
      } catch (e) {
        // encodeURIComponent throws URIError on a lone surrogate. WBProductsApi.productUrl
        // is the one place that decision normally lives, and it is exactly what is missing
        // here; its answer is the same either way — such a handle addresses no product, so
        // there is nowhere to navigate.
      }
      return;
    }

    var handle = normalizeHandle(requested);
    if (!handle || handle === currentHandle) return;
    // Already switching: remember this colour and apply it when the current one lands, rather
    // than dropping a click whose default navigation has already been suppressed.
    if (switching) { queued = { handle: handle, opts: opts }; return; }

    // Every fallback below navigates here, and fetchSwitchHtml requests off it. Null means
    // the handle cannot be encoded and so cannot address a product (see productUrl): there
    // is no fragment to fetch and no page to fall back to, so there is nothing to do.
    var url = productUrl(handle);
    if (!url) return;

    switching = true;
    showLoading();

    // Remember the current size so we can try to keep it in the new colour.
    var activeSize = document.querySelector('.fashion-info-sticky ul.product-sizes li.active');
    var previousSizeName = activeSize ? activeSize.getAttribute('data-title') : null;

    var productFetch = window.WBProductsApi.fetchByHandles([handle]);
    var htmlFetch = fetchSwitchHtml(handle);

    withTimeout(Promise.all([productFetch, htmlFetch]), SWITCH_TIMEOUT_MS).then(function(results) {
      var product = results[0] && results[0][0];
      var html = results[1];
      if (!product || !html) { navigateTo(fallbackUrl(url)); return; }

      var doc = new DOMParser().parseFromString(html, 'text/html');

      // The notify-me modal is rendered for every colour that allows it, but a shopper who
      // landed on a disable_notify colour (discontinued / birthday-edition) has a page with no
      // modal at all. Swapping a notify-me button onto that page would hand them a control
      // that opens nothing, so take the normal navigation instead — same fallback this whole
      // function uses for anything it cannot reproduce faithfully.
      if (fragmentNeedsNotifyModal(doc) && !document.getElementById('notify-me-wrapper')) {
        navigateTo(fallbackUrl(url));
        return;
      }

      // Swap server-rendered fragments. All DOM replacement happens here, before anything
      // binds handlers below — a handler bound to a node that a later swap replaces would
      // silently operate on a detached element.
      var colorName = swapColorName(doc);
      swapBreadcrumb(doc);
      swapDesktopImages(doc);
      swapMobileImages(doc);
      swapZoomGallery(doc);
      swapTopBadge(doc);
      swapPrice(doc);
      swapChanger(doc);
      swapSizes(doc);
      swapAtc(doc);
      swapNotify(doc);
      swapProductTabs(doc);
      swapKeyFeatures(doc);
      swapVariantIds(doc);
      swapHeadMeta(doc);
      swapTrackingData(doc);

      var metaTitleEl = doc.getElementById('wb-switch-meta-title');
      var metaTitle = metaTitleEl ? metaTitleEl.textContent.trim() : '';

      // Re-bind interactive behaviour + apply data-driven updates.
      reinitSizeHandlers();
      reinitNotifyHandlers(doc);
      reselectSize(previousSizeName);
      updateFormProductId(product);
      updateWishlist(product);
      updateBestPoints(product);
      swapFitFinder(doc);
      updateActiveColorSwatch(handle);
      updateTitle(colorName, metaTitle, product.title);
      // A different colour was requested while this one was in flight, so this markup is
      // about to be painted over by the replay in the finalizer below. Everything that
      // reaches past the DOM is therefore skipped as one group: the address bar would push a
      // history entry for a colour the shopper never sees, the pageview would report a path
      // the address bar was deliberately not set to (updateTracking sends productUrl(handle),
      // and its own comment relies on updateUrl having run), recently-viewed would record a
      // colour that was never shown, and complete-the-look would fire a fetch the replay
      // supersedes a moment later. The replay runs all four for the colour that does land.
      //
      // Guarded on the handles differing because a queue holding *this* handle is dropped by
      // the finalizer, and then none of them would ever run.
      if (!queued || queued.handle === handle) {
        updateUrl(handle, opts.pushState);
        updateTracking(product);
        updateRecentlyViewed(handle);
        updateCompleteTheLook(product);
      }

      currentHandle = handle;
      document.dispatchEvent(new CustomEvent('wb:colorswitch', { detail: { product: product, handle: handle } }));
    }).catch(function(err) {
      console.error('WB color switch error:', err);
      navigateTo(fallbackUrl(url));
    }).then(function() {
      // Runs on every path above, including the ones that have already committed a full page
      // load. Leave that page alone: clearing the loading state or starting a queued switch
      // there swaps a colour into a document the browser is unloading, so on a slow
      // connection the shopper watches the queued colour paint and then vanish in the
      // reload. `switching` deliberately stays true — nothing new should start either.
      if (navigating) return;

      switching = false;
      hideLoading();

      // A colour clicked while this switch was running: the swatch's default navigation was
      // already suppressed, so dropping it would leave the click doing nothing at all. Only
      // the most recent one is kept — intermediate colours the shopper clicked past are not
      // worth a round trip. Replayed with its own opts (see `queued`).
      var next = queued;
      queued = null;
      if (next && next.handle !== currentHandle) switchColor(next.handle, next.opts);
    });
  }

  // Warm the caches for one or more handles so a subsequent switchColor resolves from
  // memory. Fetches both the GraphQL product data and the ?view=pdp-switch fragment (the
  // fragment is the real gate on the switch). Skips the current colour and de-dupes via
  // the underlying caches, so calling it repeatedly on hover is cheap. Accepts a single
  // handle or an array.
  function preload(handles) {
    if (!handles) return;
    if (typeof handles === 'string') handles = [handles];
    var list = handles
      .map(function(h) { return normalizeHandle(h); })
      .filter(function(h) { return h && h !== currentHandle; });
    // Without WBProductsApi normalizeHandle returns null for every entry, so an empty list is
    // also how "the API is missing" arrives here — there is nothing to warm either way, and
    // by this line the API is known to be present. Unlike switchColor, which degrades to a
    // full page load, a prefetch that cannot run simply does not run.
    if (!list.length) return;
    window.WBProductsApi.fetchByHandles(list);
    list.forEach(function(h) { fetchSwitchHtml(h); });
  }

  // One definition of how a handle-ish string becomes a handle. WBProductsApi.normalizeHandle
  // is the canonical implementation — it strips an origin, a /products/ prefix, query and
  // hash, surrounding slashes, and lowercases — and wb-products-api.js loads immediately
  // before this file (layout/theme.liquid:290-291), so it is present in practice. Routing
  // every site here keeps the swatch lookup, the active-swatch comparison and the cache key
  // agreeing on one answer; they previously used three different rules and disagreed on
  // case, which left no swatch marked active after a switch.
  //
  // Deliberately delegates rather than carrying a local copy of the rule: a second
  // implementation is the drift this exists to prevent. Returning null when that script is
  // missing is the right degradation, not a loss — switchColor already bails to a full page
  // load without WBProductsApi, and a null here makes swatchHandle return null too, so
  // onSwatchClick returns before preventDefault and the swatch's own href navigates normally.
  function normalizeHandle(value) {
    var api = window.WBProductsApi;
    return api && api.normalizeHandle ? api.normalizeHandle(value) : null;
  }

  // The canonical URL for a handle, encoded the same way fetchSwitchHtml and
  // WBProductsApi.fetchByHandleRest request it. The navigation fallbacks used to
  // concatenate the handle raw while every fetch encoded it, so the two disagreed about
  // which product they were addressing — a click whose propagation had already been
  // stopped could land on a URL the fetch had never tried.
  //
  // Delegates to WBProductsApi for the same reason normalizeHandle does (see above): the
  // local copy and the API's agreed on the happy path and disagreed on the failure — one
  // treated an unencodable handle as a settled miss, the other built a malformed URL out of
  // it and fetched/navigated to that. Null is the single failure answer, and it means the
  // handle cannot address a product at all.
  function productUrl(handle) {
    var api = window.WBProductsApi;
    return api && api.productUrl ? api.productUrl(handle) : null;
  }

  // The locale prefix the storefront is served under — '/' on most shops, '/es/' on the
  // EU store's Spanish storefront. Deliberately a second copy of
  // WBProductsApi.localeRoot rather than a delegation: its only caller is the degradation
  // path in switchColor, which by definition runs when WBProductsApi is not there.
  // window.Shopify.routes.root is written by Shopify and needs neither script.
  function localeRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (typeof root !== 'string' || root.charAt(0) !== '/') return '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  // Where a failed switch should actually land. A colour clicked while this one was in
  // flight is the one the shopper asked for last and the one whose own click had its default
  // navigation suppressed — falling back to the in-flight handle drops it silently and puts
  // them on a colour they already clicked past. The finalizer cannot rescue this: it leaves
  // a committed navigation alone on purpose.
  function fallbackUrl(url) {
    if (!queued) return url;
    return productUrl(queued.handle) || url;
  }

  // Upper bound on a single switch. fetch() has no timeout of its own, so a request that
  // stalls rather than fails left `switching` true for good: the loading overlay stayed
  // painted and every later swatch click was preventDefault'd and dropped into `queued`,
  // with not even the anchor's href left as an escape. The in-flight requests are not
  // aborted — the page is navigating away, and threading an AbortController through
  // WBProductsApi's batching and REST fallback buys nothing once it is.
  var SWITCH_TIMEOUT_MS = 10000;

  function withTimeout(promise, ms) {
    return new Promise(function(resolve, reject) {
      var timer = setTimeout(function() {
        reject(new Error('wb-color-switch: timed out after ' + ms + 'ms'));
      }, ms);
      promise.then(
        function(value) { clearTimeout(timer); resolve(value); },
        function(error) { clearTimeout(timer); reject(error); }
      );
    });
  }

  // Commit a full page load. Flagged so the in-flight switch stops touching a document that
  // is being unloaded — window.location.href is queued, not instant, and everything after it
  // otherwise runs as normal for as long as the navigation takes.
  function navigateTo(url) {
    if (!url) return;
    navigating = true;
    window.location.href = url;
  }

  // Resolve the product handle for a hovered/focused/clicked node inside a colour swatch.
  // Mirrors how createHtml builds swatches (index.layout.js): picture swatches carry the
  // handle on <a id="variant-{handle}">, colour-dot swatches on
  // .sidebar--color-item-bg[data-handle] (which may be a raw handle, /products/{handle}, or
  // a full product URL — normalizeHandle reduces all three to the same answer).
  function swatchHandle(node) {
    if (!node || !node.closest) return null;
    var link = node.closest('a[id^="variant-"]');
    if (link) return normalizeHandle(link.id.slice('variant-'.length));
    var box = node.closest('.sidebar--color-item-box, .item');
    if (box) {
      var bg = box.querySelector('.sidebar--color-item-bg[data-handle]');
      if (bg) return normalizeHandle(bg.getAttribute('data-handle'));
    }
    return null;
  }

  // Warm on first intent over a swatch. Delegated on document so it covers swatches added
  // after load (async Tagalys render + "show more"). Cheap and idempotent: caches de-dupe,
  // and lastPrefetch skips re-running the DOM walk while the pointer sits on one swatch.
  function onSwatchIntent(e) {
    var t = e.target;
    if (!t || !t.closest || !t.closest(SWATCH_CONTAINERS)) return;
    // Same gate as the click handler: on a rail the swap cannot service the warmed data is
    // never read, so hovering it would spend requests for nothing. Checked after the target
    // test — pointerover fires continuously as the pointer crosses the page, and only a hit
    // on a swatch rail should cost a second lookup.
    if (!t.closest(SWITCH_TARGET)) return;
    var handle = swatchHandle(t);
    if (!handle || handle === currentHandle || handle === lastPrefetch) return;
    lastPrefetch = handle;
    preload(handle);
  }

  // Colour swatch clicks. Delegated from document rather than wired into each swatch's markup:
  // the rails are rebuilt client-side (index.layout.js: createHtml / createFirstElement, plus
  // "show more" and the async Tagalys render), so there is no moment at which every swatch
  // exists to bind — and no product handle has to be interpolated into an inline onclick.
  // It also covers the landing colour's own swatch, which createFirstElement renders as a
  // plain link: that was the one swatch in the rail still doing a full page load, so A→B→A
  // navigated on the last hop while every other hop switched in place.
  //
  // Only a plain primary click is intercepted. A modifier click or a middle/auxiliary click
  // falls through to the anchor's href, so opening a colour in a new tab or window keeps
  // working.
  //
  // Bound in the capture phase, and propagation is stopped once this claims the click. One of
  // the third-party tags on the PDP listens for link clicks in the bubble phase and navigates
  // by assigning location itself, which preventDefault cannot stop — so with a bubble-phase
  // listener here the first switch to a colour reliably lost the race and became a full page
  // load. Claiming the event before it reaches any other listener is the only thing that
  // reliably wins. Colour-switch tracking is not lost with it: updateTracking re-fires the
  // detail event and the pageview from the swap itself.
  //
  // Capture at document is the earliest point the event is observable, so stopping
  // propagation here suppresses *every* other handler for that click, at any phase — there
  // is no downstream fallback if this claims a click it cannot service. Every condition
  // switchColor could bail on therefore has to be tested here first, before propagation is
  // stopped, so a click this cannot switch still reaches the anchor's own href and every
  // other listener untouched.
  //
  // The one click deliberately claimed and dropped is a click on the colour already showing:
  // switchColor returns on `handle === currentHandle`, and doing nothing is the point — the
  // alternative is reloading the page onto the colour the shopper is already looking at.
  function onSwatchClick(event) {
    // Something upstream already cancelled this click — an overlay, consent or modal guard
    // suppressing interaction with the page underneath. Capture at window runs before capture
    // at document, as does any document-level capture listener registered before ours, so
    // this is reachable. Claiming a cancelled click would perform a full in-place swap for an
    // interaction that was explicitly called off, and stopImmediatePropagation() would leave
    // nothing able to observe it.
    if (event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (typeof event.button === 'number' && event.button !== 0) return;
    var t = event.target;
    if (!t || !t.closest || !t.closest(SWATCH_CONTAINERS)) return;
    // After the target test so an ordinary click elsewhere on the page costs nothing.
    if (!t.closest(SWITCH_TARGET)) return;
    var handle = swatchHandle(t);
    if (!handle) return;
    // A full page load is already committed, so switchColor would drop this click — and the
    // document can outlive the navigation it was committed for: bfcache restores it on Back
    // in Safari and Firefox, a declined beforeunload or a press of Stop keeps it. Returning
    // before preventDefault leaves the swatch's own href doing the navigating instead of the
    // rail going silently dead. (The pageshow handler in init clears the flag on a restore,
    // so in-place switching comes back too; this covers the paths that give no such signal.)
    if (navigating) return;
    // switchColor cannot address a product it has no URL for (see productUrl) and would
    // return with the click already cancelled. The href the server rendered is the better
    // answer for a handle this cannot encode.
    if (!productUrl(handle)) return;
    event.preventDefault();
    // Implies stopPropagation(); shipped in every browser that supports the capture flag
    // this listener is registered with.
    event.stopImmediatePropagation();
    switchColor(handle);
  }

  var initialised = false;

  function init() {
    // init is public, so a second call is possible. Without this the duplicate click
    // listener would be invisible — the first one's stopImmediatePropagation() prevents the
    // second from ever running — while the duplicate popstate listener quietly fired
    // switchColor twice per back-button press, the second landing while `switching` was
    // still true and being stashed as `queued`.
    if (initialised) return;
    initialised = true;

    currentHandle = getHandleFromUrl();
    if (currentHandle) history.replaceState({ handle: currentHandle }, '', window.location.href);

    window.addEventListener('popstate', function(e) {
      var h = (e.state && e.state.handle) || getHandleFromUrl();
      if (h && h !== currentHandle) switchColor(h, { pushState: false });
    });

    // A committed navigation does not always end the document. Safari and Firefox restore it
    // from bfcache when the shopper comes Back, and `navigating` / `switching` would still be
    // set on the restored page — leaving the loading state painted on and every swatch stuck
    // on the href fallback for the rest of its life. `persisted` is what distinguishes a
    // restore from a first paint, on which none of this has run yet and all of it is already
    // false. The DOM comes back exactly as it was frozen, so currentHandle still describes it.
    window.addEventListener('pageshow', function(e) {
      if (!e.persisted) return;
      navigating = false;
      switching = false;
      queued = null;
      hideLoading();
    });

    // Intent-based prefetch: warm a colour only when the shopper hovers/focuses/taps its
    // swatch, instead of eagerly fetching the whole family on load. Shoppers who never
    // switch pay nothing; those who show intent get a near-instant switch. pointerover
    // covers mouse/pen/touch-hover, focusin covers keyboard, touchstart gives touch a head
    // start just before the tap. All bubble, so document-level delegation is enough.
    ['pointerover', 'focusin', 'touchstart'].forEach(function(ev) {
      document.addEventListener(ev, onSwatchIntent, { passive: true });
    });

    document.addEventListener('click', onSwatchClick, true);
  }

  // The .wb-color-switching loading state is styled in assets/womensbest.scss.liquid.

  // --- Public API ---
  window.wbPdpColorSwitch = {
    init: init,
    switchColor: switchColor,
    preload: preload
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
